import type {
  PackageBatchGetRequest as PackageBatchGetRequestDto,
  PackageBatchGetResponse as PackageBatchGetResponseDto,
  PackageDetail as PackageDetailDto,
  PackagePage as PackagePageDto,
  PackageRevisionWriteRequest as PackageRevisionWriteDto,
  PackageWriteRequest as PackageWriteDto,
  PartialPackageRevisionWriteRequest as PartialPackageRevisionWriteDto,
} from '../api/active/generated/package';

import { randomUuid } from '../api/active/randomUuid';
import type { ActiveTransport } from '../api/active/transport';
import { createCatalogMediaApi } from '../catalog-media/catalogMediaApi';
import {
  type CatalogReadIndex,
  validateCatalogReadIndex,
} from '../quote-integration/catalogReadIndex';
import {
  notifyQuoteCatalogMutation,
  quoteCatalogSessionResource,
} from '../quote-integration/catalogSessionResource';
import type { PackageGroupPricingImpact } from './packageGroupApi';

export class PackageApiError extends Error {
  constructor(
    readonly status: number | null,
    readonly code: string | undefined,
    message: string,
    readonly fieldErrors: readonly {
      path: string;
      code?: string;
      message?: string;
    }[] = [],
  ) {
    super(message);
  }
}

export type PackageListQuery = {
  page: number;
  pageSize: number;
  search?: string;
  searchBy?: 'package' | 'device';
  status?: 'active' | 'inactive';
  packageGroupId?: string;
};

const MAX_INT64 = 9_223_372_036_854_775_807n;

function isPositiveInt64(value: string): boolean {
  return /^[1-9]\d*$/.test(value) && BigInt(value) <= MAX_INT64;
}

export function requirePackageStrongEtag(etag: string): string {
  const match = /^"(\d+)"$/.exec(etag);
  if (!match || !isPositiveInt64(match[1]))
    throw new Error('套餐 If-Match 必须是带双引号的正 int64 强 ETag');
  return etag;
}

export function requirePackageDetailEtag(
  resourceVersion: string,
  etag: string,
): string {
  if (!isPositiveInt64(resourceVersion))
    throw new Error('套餐详情 resourceVersion 不合法');
  // nginx gzip may weaken the representation ETag. The persisted version in
  // the same response remains the aggregate CAS token; only accept the weak
  // form when it names exactly that version, then send a strong If-Match.
  if (etag === `W/"${resourceVersion}"`) return `"${resourceVersion}"`;
  const strongEtag = requirePackageStrongEtag(etag);
  if (strongEtag !== `"${resourceVersion}"`)
    throw new Error('套餐详情 ETag 与 resourceVersion 不一致');
  return strongEtag;
}

/**
 * Rebuild package writes from the frozen request shape at the HTTP boundary.
 * Package detail rows intentionally carry read-only relation snapshots and
 * calculated prices; none of those fields may leak into a strict OpenAPI
 * request even if a legacy caller passes an oversized object at runtime.
 */
function contractRevisionWrite(
  body: PackageRevisionWriteDto,
): PackageRevisionWriteDto;
function contractRevisionWrite(
  body: PartialPackageRevisionWriteDto,
): PartialPackageRevisionWriteDto;
function contractRevisionWrite(
  body: PartialPackageRevisionWriteDto,
): PartialPackageRevisionWriteDto {
  const mergePricesOverride = (
    body as PartialPackageRevisionWriteDto & {
      mergePricesOverride?: boolean | null;
    }
  ).mergePricesOverride;
  return {
    name: body.name,
    sortOrder: body.sortOrder,
    ...(mergePricesOverride !== undefined ? { mergePricesOverride } : {}),
    custom: body.custom,
    items: body.items?.map((item) => ({
      deviceId: item.deviceId,
      deviceRevisionId: item.deviceRevisionId,
      ...(typeof item.replacementPoolId !== 'string' ||
      item.replacementPoolId.trim() === ''
        ? {}
        : { replacementPoolId: item.replacementPoolId }),
      quantity: item.quantity,
      sortOrder: item.sortOrder,
    })),
    coverImages: body.coverImages?.map((image) => ({
      objectId: image.objectId,
      sortOrder: image.sortOrder,
      ...(image.isPrimary === undefined ? {} : { isPrimary: image.isPrimary }),
    })),
    lightingImages: body.lightingImages?.map((image) => ({
      objectId: image.objectId,
      sortOrder: image.sortOrder,
      ...(image.isPrimary === undefined ? {} : { isPrimary: image.isPrimary }),
    })),
    publish: body.publish,
    targetPackageStatus: body.targetPackageStatus,
    ...('planMemberships' in body && body.planMemberships
      ? {
          planMemberships: {
            items: body.planMemberships.items.map((item) => ({
              planId: item.planId,
              expectedResourceVersion: item.expectedResourceVersion,
              ...(item.packageRevisionIds === undefined
                ? {}
                : { packageRevisionIds: [...item.packageRevisionIds] }),
            })),
          },
        }
      : {}),
  };
}

function contractWrite(
  body:
    | (PackageWriteDto & { packageGroupId?: string | null })
    | (PartialPackageRevisionWriteDto & {
        packageGroupId?: string | null;
        pricingImpactHash?: string;
        mergePricesOverride?: boolean | null;
      }),
): PackageWriteDto | PartialPackageRevisionWriteDto {
  const packageGroupId = body.packageGroupId;
  if ('initialRevision' in body)
    return {
      ...(packageGroupId !== undefined ? { packageGroupId } : {}),
      initialRevision: contractRevisionWrite(body.initialRevision),
    } as PackageWriteDto;
  return {
    ...contractRevisionWrite(body),
    ...(packageGroupId !== undefined ? { packageGroupId } : {}),
    ...('pricingImpactHash' in body && body.pricingImpactHash
      ? { pricingImpactHash: body.pricingImpactHash }
      : {}),
  } as PartialPackageRevisionWriteDto;
}

async function problem(response: Response): Promise<PackageApiError> {
  let value: {
    code?: string;
    errorCode?: string;
    detail?: string;
    title?: string;
    fieldErrors?: Array<{ path?: string; code?: string; message?: string }>;
  } = {};
  try {
    value = (await response.json()) as typeof value;
  } catch {
    // Keep the status when the proxy returns no problem body.
  }
  const fieldErrors = (value.fieldErrors ?? []).flatMap((item) =>
    typeof item.path === 'string' && item.path.trim() !== ''
      ? [
          {
            path: item.path,
            code: item.code,
            message: item.message,
          },
        ]
      : [],
  );
  const fieldSummary = fieldErrors.map((item) => item.path).join('、');
  const code = value.code ?? value.errorCode;
  const detail =
    response.status === 503 &&
    code === 'DEPENDENCY_UNAVAILABLE' &&
    value.detail === 'The request could not be completed safely.'
      ? '暂无法完成套餐操作，请稍后重试。'
      : (value.detail ??
        value.title ??
        `套餐请求失败（HTTP ${response.status}）。`);
  return new PackageApiError(
    response.status,
    code,
    fieldSummary ? `${detail}（字段：${fieldSummary}）` : detail,
    fieldErrors,
  );
}

function queryString(query: PackageListQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.searchBy) params.set('searchBy', query.searchBy);
  if (query.status) params.set('status', query.status);
  if (query.packageGroupId) params.set('packageGroupId', query.packageGroupId);
  return params.toString();
}

export function createPackageApi(
  transport: ActiveTransport,
  csrfToken: string | null,
) {
  const media = createCatalogMediaApi(transport, csrfToken);
  const mutationHeaders: Record<string, string> = csrfToken
    ? { 'X-CSRF-Token': csrfToken }
    : {};
  const write = async (
    path: string,
    body:
      | (PackageWriteDto & { packageGroupId?: string | null })
      | (PartialPackageRevisionWriteDto & {
          packageGroupId?: string | null;
          pricingImpactHash?: string;
          mergePricesOverride?: boolean | null;
        }),
    options: { etag?: string; idempotencyKey?: string } = {},
  ) => {
    const mutationScope = quoteCatalogSessionResource.currentScope();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Idempotency-Key': options.idempotencyKey ?? randomUuid(),
      ...mutationHeaders,
    };
    if (options.etag !== undefined)
      headers['If-Match'] = requirePackageStrongEtag(options.etag);
    const response = await transport.requestResponse(path, {
      method: 'POST',
      headers,
      body: JSON.stringify(contractWrite(body)),
    });
    if (!response.ok) throw await problem(response);
    const value = (await response.json()) as PackageDetailDto;
    notifyQuoteCatalogMutation(
      mutationScope,
      'packages',
      [value.id],
      ['plans'],
    );
    return value;
  };
  return {
    media,
    async catalogIndex(signal?: AbortSignal) {
      const response = await transport.requestResponse(
        '/packages/catalog-index',
        { signal, cache: 'no-store' },
      );
      if (!response.ok) throw await problem(response);
      return validateCatalogReadIndex(
        (await response.json()) as CatalogReadIndex,
      );
    },
    async list(query: PackageListQuery, signal?: AbortSignal) {
      const response = await transport.requestResponse(
        `/packages?${queryString(query)}`,
        { signal, cache: 'no-store' },
      );
      if (!response.ok) throw await problem(response);
      return (await response.json()) as PackagePageDto;
    },
    async get(packageId: string, signal?: AbortSignal) {
      const response = await transport.requestResponse(
        `/packages/${encodeURIComponent(packageId)}`,
        { signal, cache: 'no-store' },
      );
      if (!response.ok) throw await problem(response);
      const value = (await response.json()) as PackageDetailDto;
      return {
        value,
        etag: requirePackageDetailEtag(
          value.resourceVersion,
          response.headers.get('ETag') ?? '',
        ),
      };
    },
    async batchGet(body: PackageBatchGetRequestDto, signal?: AbortSignal) {
      const response = await transport.requestResponse('/packages:batch-get', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...mutationHeaders,
        },
        body: JSON.stringify(body),
        signal,
        cache: 'no-store',
      });
      if (!response.ok) throw await problem(response);
      return (await response.json()) as PackageBatchGetResponseDto;
    },
    async previewGroupChange(
      packageId: string,
      packageResourceVersion: string,
      packageGroupId: string | null,
      signal?: AbortSignal,
    ): Promise<PackageGroupPricingImpact> {
      const response = await transport.requestResponse(
        `/packages/${encodeURIComponent(packageId)}/package-group:price-impact-preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...mutationHeaders,
          },
          body: JSON.stringify({
            packageGroupId,
            resourceVersion: packageResourceVersion,
          }),
          signal,
          cache: 'no-store',
        },
      );
      if (!response.ok) throw await problem(response);
      return (await response.json()) as PackageGroupPricingImpact;
    },
    create: (
      body: PackageWriteDto & { packageGroupId?: string | null },
      idempotencyKey?: string,
    ) => write('/packages', body, { idempotencyKey }),
    createRevision: (
      packageId: string,
      etag: string,
      body: PartialPackageRevisionWriteDto & {
        packageGroupId?: string | null;
        pricingImpactHash?: string;
        mergePricesOverride?: boolean | null;
      },
      idempotencyKey?: string,
    ) =>
      write(`/packages/${encodeURIComponent(packageId)}/revisions`, body, {
        etag,
        idempotencyKey,
      }),
    async remove(packageId: string, etag: string, idempotencyKey?: string) {
      const mutationScope = quoteCatalogSessionResource.currentScope();
      const strongEtag = requirePackageStrongEtag(etag);
      const response = await transport.requestResponse(
        `/packages/${encodeURIComponent(packageId)}`,
        {
          method: 'DELETE',
          headers: {
            'If-Match': strongEtag,
            'Idempotency-Key': idempotencyKey ?? randomUuid(),
            ...mutationHeaders,
          },
        },
      );
      if (!response.ok) throw await problem(response);
      notifyQuoteCatalogMutation(
        mutationScope,
        'packages',
        [packageId],
        ['plans'],
      );
    },
  };
}
